/**
 * History Manager
 *
 * Manages undo/redo functionality for the puzzle builder.
 * Migrated from the legacy Csomag and Elozmeny constructor functions.
 */

import type { HistoryPackage } from '../types/puzzle.types';

/**
 * Callback type for reloading puzzle state
 */
export type ReloadPuzzleCallback = (
    xml: string,
    cellId: number,
    isHorizontal: boolean,
) => void;

/**
 * History package representing a single snapshot in the undo/redo chain.
 */
class Package implements HistoryPackage {
    id: number;
    previousId: number | null = null;
    nextId: number | null = null;
    name: string;
    puzzleXml: string;

    constructor(id: number, name: string, puzzleXml: string) {
        this.id = id;
        this.name = name;
        this.puzzleXml = puzzleXml;
    }

    toString(): string {
        return `${this.previousId ?? 'null'}:${this.id}:${
            this.nextId ?? 'null'
        }`;
    }
}

/**
 * History Manager for undo/redo operations.
 */
export class HistoryManager {
    private _packages: Package[] = [];
    private _currentPackage: Package | null = null;
    private _sequenceNumber = -1;
    private readonly _maxPackages: number;

    // Callbacks for state changes
    private _onUnsaved: (() => void) | null = null;
    private _onReload: ReloadPuzzleCallback | null = null;

    // External state references (to be set by the application)
    private _getCurrentCellId: (() => number) | null = null;
    private _getIsHorizontal: (() => boolean) | null = null;
    private _getPuzzleXml: (() => string) | null = null;

    constructor(maxPackages = 50) {
        this._maxPackages = maxPackages;
    }

    // ============================================================================
    // Configuration
    // ============================================================================

    /**
     * Set the callback for getting the current puzzle XML.
     */
    setPuzzleXmlGetter(getter: () => string): void {
        this._getPuzzleXml = getter;
    }

    /**
     * Set the callback for getting the current cell ID.
     */
    setCellIdGetter(getter: () => number): void {
        this._getCurrentCellId = getter;
    }

    /**
     * Set the callback for getting the current direction.
     */
    setDirectionGetter(getter: () => boolean): void {
        this._getIsHorizontal = getter;
    }

    /**
     * Set the callback for when puzzle becomes unsaved.
     */
    setOnUnsaved(callback: () => void): void {
        this._onUnsaved = callback;
    }

    /**
     * Set the callback for reloading puzzle state.
     */
    setOnReload(callback: ReloadPuzzleCallback): void {
        this._onReload = callback;
    }

    // ============================================================================
    // State Management
    // ============================================================================

    /**
     * Save the current state to history.
     */
    save(actionName: string): void {
        if (!this._getPuzzleXml) {
            console.warn('HistoryManager: No puzzle XML getter configured');
            return;
        }

        this._sequenceNumber++;
        const puzzleXml = this._getPuzzleXml();
        const pkg = new Package(this._sequenceNumber, actionName, puzzleXml);

        if (this._sequenceNumber > 0 && this._currentPackage) {
            // Clear forward history
            this.clearForwardHistory(this._currentPackage.id);

            // Link packages
            this.getPackageById(this._currentPackage.id)!.nextId =
                this._sequenceNumber;
            pkg.previousId = this._currentPackage.id;

            // Trigger unsaved state
            this._onUnsaved?.();
        }

        this._currentPackage = pkg;
        this._packages.push(pkg);

        // Enforce max history limit
        if (this._packages.length > this._maxPackages) {
            this._packages.shift();
            if (this._packages.length > 0) {
                this._packages[0].previousId = null;
            }
        }
    }

    /**
     * Jump to a specific history point.
     */
    jumpTo(packageId: number): void {
        const pkg = this.getPackageById(packageId);
        if (pkg) {
            this._currentPackage = pkg;
            this.reloadPuzzle(pkg);
        }
    }

    /**
     * Undo the last action.
     */
    undo(): void {
        if (this._currentPackage && this._currentPackage.previousId !== null) {
            const pkg = this.getPackageById(this._currentPackage.previousId);
            if (pkg) {
                this._currentPackage = pkg;
                this.reloadPuzzle(pkg);
            }
        }
    }

    /**
     * Redo the last undone action.
     */
    redo(): void {
        if (this._currentPackage && this._currentPackage.nextId !== null) {
            const pkg = this.getPackageById(this._currentPackage.nextId);
            if (pkg) {
                this._currentPackage = pkg;
                this.reloadPuzzle(pkg);
            }
        }
    }

    // ============================================================================
    // State Queries
    // ============================================================================

    /**
     * Check if undo is available.
     */
    get canUndo(): boolean {
        return (
            this._currentPackage !== null &&
            this._currentPackage.previousId !== null
        );
    }

    /**
     * Check if redo is available.
     */
    get canRedo(): boolean {
        return (
            this._currentPackage !== null &&
            this._currentPackage.nextId !== null
        );
    }

    /**
     * Get the undo action label.
     */
    get undoLabel(): string {
        if (!this._currentPackage) return '';
        return `Visszavonás: ${this._currentPackage.name} (Ctrl+Z)`;
    }

    /**
     * Get the redo action label.
     */
    get redoLabel(): string {
        if (!this._currentPackage?.nextId) return '';
        const nextPkg = this.getPackageById(this._currentPackage.nextId);
        if (!nextPkg) return '';
        return `Ismét: ${nextPkg.name} (Ctrl+Y)`;
    }

    /**
     * Get all packages for display.
     */
    get packages(): ReadonlyArray<HistoryPackage> {
        return this._packages;
    }

    /**
     * Get the current package.
     */
    get currentPackage(): HistoryPackage | null {
        return this._currentPackage;
    }

    // ============================================================================
    // Render to HTML
    // ============================================================================

    toHtml(): string {
        let html = `<span class="cim">Előzmények (${this._maxPackages})</span>`;

        for (const pkg of this._packages) {
            html += `<a href="javascript:elozmenyUgras(${pkg.id})">`;

            if (pkg.id === this._currentPackage?.id) {
                html += `<b>${pkg.name}</b>`;
            } else {
                html += pkg.name;
            }

            html += '</a>';
        }

        return html;
    }

    // ============================================================================
    // Private Methods
    // ============================================================================

    private getPackageById(id: number | null): Package | null {
        if (id === null) return null;
        return this._packages.find((pkg) => pkg.id === id) ?? null;
    }

    private clearForwardHistory(fromId: number): void {
        this._packages = this._packages.filter((pkg) => pkg.id <= fromId);
    }

    private reloadPuzzle(pkg: Package): void {
        if (!this._onReload) {
            console.warn('HistoryManager: No reload callback configured');
            return;
        }

        const cellId = this._getCurrentCellId?.() ?? 0;
        const isHorizontal = this._getIsHorizontal?.() ?? true;

        this._onReload(pkg.puzzleXml, cellId, isHorizontal);
    }
}
